/**
 * SessionControls — the picker on the demo intro splash that lets a
 * caregiver tailor the upcoming session.
 *
 * Two choices:
 *   1. STYLE — how the memories are ordered
 *      • Curated journey (default) — the playlist the family curated
 *      • Family voices mixed — smart shuffle (no two contributors back-to-back)
 *      • Through the years — chronological (oldest → newest)
 *   2. LENGTH — how long the session runs
 *      • 15 min · 30 min · 60 min · Full
 *      • For dementia patients with hundreds of memories, capping the
 *        session prevents 6-hour linear playback fatigue and keeps every
 *        visit fresh.
 *
 * Pure presentational — state lives in the parent demo page so the
 * playlist fetch can react to changes.
 */
export function SessionControls({
    style, onStyleChange,
    capMinutes, onCapMinutesChange,
    estimatedSec, memoryCount,
    theme = "dark",
}) {
    const isDark = theme !== "light";

    const styleOptions = [
        { id: "full",          label: "Curated journey",  caption: "Family's chosen order" },
        { id: "smart",         label: "Family voices",    caption: "Mixed so no two narrators run back-to-back" },
        { id: "chronological", label: "Through the years", caption: "Oldest memories first" },
    ];

    const lengthOptions = [
        { value: 15,   label: "15 min" },
        { value: 30,   label: "30 min" },
        { value: 60,   label: "60 min" },
        { value: null, label: "Full"   },
    ];

    const styleBtn = (active) => isDark
        ? (active
            ? "border-[#A8B5E0] bg-[#A8B5E0]/15 text-white"
            : "border-white/15 bg-white/[0.03] text-white/70 hover:text-white hover:border-white/30")
        : (active
            ? "border-[#C97B8E] bg-[#C97B8E]/12 text-[#3A2A30]"
            : "border-[#C97B8E]/20 bg-white/40 text-[#3A2A30]/65 hover:text-[#3A2A30] hover:border-[#C97B8E]/40");

    const lengthBtn = (active) => isDark
        ? (active
            ? "bg-[#A8B5E0] text-[#1a1c25] border-[#A8B5E0]"
            : "bg-transparent text-white/70 hover:text-white border-white/20 hover:border-white/40")
        : (active
            ? "bg-[#C97B8E] text-white border-[#C97B8E]"
            : "bg-transparent text-[#3A2A30]/65 hover:text-[#3A2A30] border-[#C97B8E]/25 hover:border-[#C97B8E]/55");

    const subText = isDark ? "text-white/55" : "text-[#3A2A30]/60";
    const labelText = isDark ? "text-white/70" : "text-[#3A2A30]/70";
    const minutes = estimatedSec ? Math.max(1, Math.round(estimatedSec / 60)) : null;

    return (
        <div className="mt-10 mx-auto max-w-2xl text-left" data-testid="session-controls">
            {/* STYLE */}
            <p className={`text-[10px] uppercase tracking-[0.32em] mb-3 ${labelText}`}>
                Session style
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-7">
                {styleOptions.map(opt => (
                    <button
                        key={opt.id}
                        type="button"
                        onClick={() => onStyleChange?.(opt.id)}
                        className={`text-left px-4 py-3 rounded-xl border transition-all ${styleBtn(style === opt.id)}`}
                        data-testid={`session-style-${opt.id}`}
                        aria-pressed={style === opt.id}
                    >
                        <p className="text-sm font-medium leading-tight">{opt.label}</p>
                        <p className={`text-[11px] mt-0.5 ${subText}`}>{opt.caption}</p>
                    </button>
                ))}
            </div>

            {/* LENGTH */}
            <p className={`text-[10px] uppercase tracking-[0.32em] mb-3 ${labelText}`}>
                Session length
            </p>
            <div className="flex flex-wrap gap-2">
                {lengthOptions.map(opt => (
                    <button
                        key={String(opt.value)}
                        type="button"
                        onClick={() => onCapMinutesChange?.(opt.value)}
                        className={`px-4 py-1.5 rounded-full border text-xs font-medium tracking-wide transition-all ${lengthBtn(capMinutes === opt.value)}`}
                        data-testid={`session-length-${opt.value === null ? "full" : opt.value}`}
                        aria-pressed={capMinutes === opt.value}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {/* Computed footnote */}
            {minutes !== null && memoryCount !== null && (
                <p className={`mt-5 text-xs ${subText}`} data-testid="session-stats">
                    Your session: ~{minutes} min · {memoryCount} memories
                </p>
            )}
        </div>
    );
}
